import styled from '@emotion/styled'
import { Button } from 'app/components/Common/Button'
import { Props as ImageProps } from 'app/components/Common/Image'
import { Separator } from 'app/components/Common/Separator'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { useVocabularyData } from 'app/utils/vocabulary'
import { uniqBy } from 'lodash'
import React, { memo } from 'react'
import LazyLoad from 'react-lazyload'

import { Item, Props as BenefitsItem } from './Item'

export interface Props {
  benefits?: BenefitsItem[]
  description?: string
  IBEID?: number
  image?: ImageProps
  label?: string
  languageCode: string
  languagePrefix: string
  separator?: boolean
}

export const VisualBenefits = memo(function VisualBenefits({
  benefits,
  description,
  IBEID,
  image,
  label,
  languageCode,
  languagePrefix,
  separator = true,
}: Props) {
  if (benefits && benefits?.length < 1) {
    return null
  }

  return (
    <Container>
      {separator ? <Separator /> : null}
      <Wrapper row stretch wrap>
        <LeftSide dial={5}>
          <Wrap>
            {description ? (
              <Description dangerouslySetInnerHTML={{ __html: description }} />
            ) : null}
            {label ? <Label>{label}</Label> : null}
            <Button
              arrow
              label={useVocabularyData('book-now', languageCode)}
              URL={`https://be.bookingexpert.it/book/simple/step1?hotel=${IBEID}&lang=${
                languagePrefix ? languagePrefix : 'en'
              }`}
              rel="noopener"
              target="_blank"
              variant="corners"
            />
          </Wrap>
        </LeftSide>
        <RightSide>
          {image ? (
            <LazyLoad>
              <Background style={{ backgroundImage: `url(${image})` }} />
            </LazyLoad>
          ) : null}
          <Checklist dial={5}>
            <div>
              {uniqBy(benefits, 'label')?.map((item, index) => (
                <Item key={index} {...item} />
              ))}
            </div>
          </Checklist>
        </RightSide>
      </Wrapper>
    </Container>
  )
})

const Container = styled.section`
  margin: -3.75rem auto 0;
`

const Wrapper = styled(FlexBox)``

const LeftSide = styled(FlexBox)`
  width: 50%;
  background: ${({ theme }) => theme.colors.variants.neutralLight3};

  @media (max-width: 767px) {
    width: 100%;
    text-align: center;
  }
`

const Wrap = styled.div`
  max-width: 28.75rem;
  padding: 3.75rem 1.5rem;

  @media (max-width: 1023px) {
    max-width: none;
    padding: 3rem 1.5rem;
  }
`

const Description = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 2.5rem;
  font-weight: 500;
  line-height: 3.375rem;
  b,
  strong {
    color: ${({ theme }) => theme.colors.variants.primaryDark1};
  }
`

const Label = styled.div`
  color: ${({ theme }) => theme.colors.variants.neutralDark4};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1rem;
  line-height: 1.875rem;
  margin-top: 1.5rem;
`

const RightSide = styled.div`
  width: 50%;
  padding: 3.75rem;
  position: relative;

  @media (max-width: 1023px) {
    padding: 2.25rem;
  }

  @media (max-width: 767px) {
    width: 100%;
    padding: 1.5rem;
  }
`

const Background = styled.div`
  width: 100%;
  height: 100%;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  position: absolute;
  top: 0;
  left: 0;
`

const Checklist = styled(FlexBox)`
  background: ${({ theme }) => theme.colors.variants.neutralLight4};
  padding: 5.125rem 10vw;
  position: relative;
  z-index: 2;

  @media (max-width: 1023px) {
    padding: 3.75rem 2.25rem;
  }

  @media (max-width: 767px) {
    padding: 2.25rem 1.5rem;
  }
`
